import { MsalProvider, useMsal } from '@azure/msal-react';
import { useEffect } from 'react';
import { HashRouter, Route, Routes } from 'react-router-dom';
import { isAdmin, isAffiliate } from './lib/authorization';
import { adClient } from './lib/http-client';
import { useFirebaseUserTracking } from './lib/useFirebaseUserTracking';

import ProtectedRoute from './components/protected-route/protected-route';
import insights from './lib/insights';
import AdminPage from './pages/admin';
import AffiliatePage from './pages/affiliate';
import AffiliateDetailsPage from './pages/affiliate-details';
import CustomerDetailsPage from './pages/customer-details';
import DevPage from './pages/dev';
import PostLoginPage from './pages/postlogin';
import ProfilePage from './pages/profile';
import WelcomePage from './pages/welcome';
import FirmInfoPage from './pages/firmInfo';
import './app.scss';

function MsalRedirectHandler({ children }: { children: React.ReactNode }) {
   const { instance } = useMsal();

   useEffect(() => {
      instance
         .handleRedirectPromise()
         .then((response) => {
            if (response && response.account) {
               instance.setActiveAccount(response.account);
            }
         })
         .catch((error) => {
            console.error('MSAL redirect error:', error);
         });
   }, [instance]);

   return <>{children}</>;
}

export function App(): JSX.Element {
   useEffect(() => {
      insights.enableInsights();
   }, []);

   // Add this line to track user in Firebase Analytics
   useFirebaseUserTracking();

   return (
      <MsalProvider instance={adClient}>
         <MsalRedirectHandler>
            <HashRouter>
               <Routes>
                  <Route path="/" element={<WelcomePage />} />
                  <Route path="/postlogin" element={<PostLoginPage />} />
                  <Route
                     path="/firm-info"
                     element={
                        <ProtectedRoute
                           permission={isAdmin}
                           subRoute={<FirmInfoPage />}
                        />
                     }
                  />
                  <Route path="/client-details">
                     <Route
                        path=":id"
                        element={
                           <ProtectedRoute
                              permission={isAffiliate}
                              subRoute={<CustomerDetailsPage />}
                           />
                        }
                     />
                  </Route>
                  <Route path="/admin">
                     <Route
                        path=":organizationName?"
                        element={
                           <ProtectedRoute
                              permission={isAdmin}
                              subRoute={<AdminPage />}
                           />
                        }
                     />
                  </Route>
                  <Route path="/affiliate-details">
                     <Route
                        path=":id"
                        element={
                           <ProtectedRoute
                              permission={isAdmin}
                              subRoute={<AffiliateDetailsPage />}
                           />
                        }
                     />
                  </Route>
                  <Route path="/profile" element={<ProfilePage />} />
                  <Route
                     path="/dev"
                     element={
                        <ProtectedRoute
                           permission={isAdmin}
                           subRoute={<DevPage />}
                        />
                     }
                  />
                  <Route
                     path="/affiliate"
                     element={
                        <ProtectedRoute
                           permission={isAffiliate}
                           subRoute={<AffiliatePage />}
                        />
                     }
                  />
               </Routes>
            </HashRouter>
         </MsalRedirectHandler>
      </MsalProvider>
   );
}
